/**
 * Shared authentication utilities and constants
 * Used by both frontend and backend for consistent role checking
 */

export const AUTH_CONFIG = {
  customNamespace: 'https://jayone.org',
  rolesClaim: 'roles',
  adminRoles: ['masodj-admin', 'admin', 'Admin'], // masodj-admin is primary, admin/Admin for backward compatibility
} as const;

/**
 * Extract roles from JWT token payload
 * Checks multiple claim locations for compatibility
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function extractRolesFromToken(payload: Record<string, any>): string[] {
  const rolesClaimKey = `${AUTH_CONFIG.customNamespace}/${AUTH_CONFIG.rolesClaim}`;

  // 1. Try custom namespace roles (e.g., https://jayone.org/roles)
  if (payload[rolesClaimKey] && Array.isArray(payload[rolesClaimKey])) {
    return payload[rolesClaimKey];
  }

  // 2. Try permissions array
  if (payload.permissions && Array.isArray(payload.permissions)) {
    return payload.permissions;
  }

  // 3. Try app_metadata roles
  const appMetadata = payload[`${AUTH_CONFIG.customNamespace}/app_metadata`];
  if (appMetadata?.roles && Array.isArray(appMetadata.roles)) {
    return appMetadata.roles;
  }

  // 4. Try default roles claim
  if (payload.roles && Array.isArray(payload.roles)) {
    return payload.roles;
  }

  return [];
}

/**
 * Check if roles array contains admin role
 * Case-insensitive check
 */
export function hasAdminRole(roles: string[]): boolean {
  const lowerRoles = roles.map(r => r.toLowerCase());
  return AUTH_CONFIG.adminRoles.some(adminRole =>
    lowerRoles.includes(adminRole.toLowerCase())
  );
}

/**
 * Check if user has admin role from token payload
 * Combines role extraction and admin check
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isAdminUser(payload: Record<string, any>): boolean {
  const roles = extractRolesFromToken(payload);
  return hasAdminRole(roles);
}
